import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axios';
import toast from 'react-hot-toast';
import PaymentCollectionModal from '../../components/admin/payments/PaymentCollectionModal';
import EditPaymentModal from '../../components/admin/payments/EditPaymentModal';
import ChecksList from '../../components/admin/payments/ChecksList';
import CompanyDocuments from '../../components/company/CompanyDocuments';
import CompanyChecksList from '../../components/admin/payments/CompanyChecksList';
import CompanyOrdersList from '../../components/admin/payments/CompanyOrdersList';

// ============================================
// Component: AdminCompanyDebt
// Purpose: Detailed debt view for a single company (Admin)
// Enhanced Features:
// - Full payment history with checks
// - Credit management
// - Send payment reminders
// - Download detailed reports
// - Audit log (future)
// ============================================

const AdminCompanyDebt = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [activeTab, setActiveTab] = useState('payments'); // payments, orders, checks, activity

  // Edit payment state
  const [showEditModal, setShowEditModal] = useState(false);
  const [paymentToEdit, setPaymentToEdit] = useState(null);

  // Cancel payment state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [paymentToCancel, setPaymentToCancel] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Discount modal state
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);

  useEffect(() => {
    fetchCompanyDetails();
  }, [companyId]);

  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/admin/payments/company/${companyId}`);
      setData(response.data);
    } catch (error) {
      toast.error('שגיאה בטעינת נתוני החברה');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    fetchCompanyDetails();
    setShowCollectionModal(false);
  };

  const handleEditSuccess = () => {
    fetchCompanyDetails();
    setShowEditModal(false);
    setPaymentToEdit(null);
  };

  const handleDownloadReceipt = async (paymentId, receiptNumber) => {
    try {
      const response = await axiosInstance.get(`/api/admin/payments/${paymentId}/receipt/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt-${receiptNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('הקבלה הורדה בהצלחה');
    } catch (error) {
      toast.error('שגיאה בהורדת הקבלה');
    }
  };

  const handleApprovePayment = async (paymentId) => {
    try {
      await axiosInstance.put(`/api/admin/payments/${paymentId}/approve`);
      toast.success('התשלום אושר בהצלחה');
      fetchCompanyDetails();
    } catch (error) {
      toast.error('שגיאה באישור התשלום');
    }
  };

  const handleRejectPayment = async (paymentId) => {
    const reason = prompt('אנא הזן סיבת דחייה:');
    if (!reason) return;

    try {
      await axiosInstance.put(`/api/admin/payments/${paymentId}/reject`, {
        rejection_reason: reason
      });
      toast.success('התשלום נדחה');
      fetchCompanyDetails();
    } catch (error) {
      toast.error('שגיאה בדחיית התשלום');
    }
  };

  // Apply general discount to company debt
  const handleApplyDiscount = async () => {
    const amount = parseFloat(discountAmount);
    if (!amount || amount <= 0) {
      toast.error('נא להזין סכום הנחה תקין');
      return;
    }
    if (!discountReason.trim()) {
      toast.error('נא להזין סיבת הנחה');
      return;
    }

    try {
      setApplyingDiscount(true);
      await axiosInstance.post(`/api/admin/payments/company/${companyId}/discount`, {
        amount: amount,
        reason: discountReason
      });
      toast.success(`הנחה בסך ${formatCurrency(amount)} הוחלה בהצלחה`);
      setShowDiscountModal(false);
      setDiscountAmount('');
      setDiscountReason('');
      fetchCompanyDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בהחלת ההנחה');
    } finally {
      setApplyingDiscount(false);
    }
  };

  const handleCancelPayment = async () => {
    if (!cancellationReason.trim()) {
      toast.error('אנא הזן סיבת ביטול');
      return;
    }

    try {
      setCancelling(true);
      await axiosInstance.post(`/api/admin/payments/${paymentToCancel.id}/cancel`, {
        cancellation_reason: cancellationReason.trim()
      });

      toast.success('התשלום בוטל בהצלחה');
      setShowCancelModal(false);
      setPaymentToCancel(null);
      setCancellationReason('');

      // Refresh payment details
      await fetchCompanyDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בביטול התשלום');
    } finally {
      setCancelling(false);
    }
  };

  const openCancelModal = (payment) => {
    setPaymentToCancel(payment);
    setShowCancelModal(true);
  };

  const openEditModal = (payment) => {
    setPaymentToEdit(payment);
    setShowEditModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: 'מזומן',
      check: 'צ\'ק',
      bank_transfer: 'העברה בנקאית',
      discount: 'הנחה'
    };
    return labels[method] || method;
  };

  const getStatusBadge = (status) => {
    const badges = {
      approved: <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">אושר</span>,
      pending: <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">ממתין לאישור</span>,
      rejected: <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">נדחה</span>
    };
    return badges[status] || status;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p>לא נמצאו נתונים</p>
      </div>
    );
  }

  const { company, credit_info, payments } = data;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" dir="rtl">
      {/* Back Button */}
      <button
        onClick={() => navigate('/admin/companies-debt')}
        className="mb-4 text-primary-600 hover:text-primary-800 flex items-center gap-2"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        חזרה לרשימת החברות
      </button>

      {/* Company Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{company.name}</h1>
            <p className="text-gray-600 text-sm">ח.פ: {company.business_number}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowCollectionModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 font-medium text-sm"
            >
              רשום גבייה חדשה
            </button>
            <button
              onClick={() => setShowDiscountModal(true)}
              className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 font-medium text-sm"
            >
              הנחה על חוב
            </button>
            <button
              onClick={() => toast.info('תכונה בפיתוח')}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 font-medium text-sm"
            >
              שלח תזכורת
            </button>
            <button
              onClick={() => toast.info('תכונה בפיתוח')}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 font-medium text-sm"
            >
              הורד דוח
            </button>
          </div>
        </div>
      </div>

      {/* Credit Summary Cards */}
      {credit_info && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 mb-1">מסגרת אשראי</div>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(credit_info.credit_limit || 0)}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 mb-1">יתרת חוב (כולל מע"מ)</div>
              <div className={`text-2xl font-bold ${(credit_info.current_balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(credit_info.current_balance || 0)}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 mb-1">סה"כ נגבה</div>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(credit_info.collected_amount || 0)}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 mb-1">סה"כ זיכויים</div>
              <div className="text-2xl font-bold text-orange-600">
                {formatCurrency(credit_info.refunds_total || 0)}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600 mb-1">אשראי זמין</div>
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(credit_info.available_credit || 0)}
              </div>
            </div>
          </div>

          {/* Debt Breakdown */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="text-sm font-semibold text-gray-700 mb-2">פירוט חוב:</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div>
                <span className="text-gray-500">חוב פתיחה: </span>
                <span className="font-semibold text-blue-600">
                  {formatCurrency(credit_info.opening_balance || 0)}
                </span>
              </div>
              <div>
                <span className="text-gray-500">הזמנות (כולל מע"מ): </span>
                <span className="font-semibold text-gray-900">
                  +{formatCurrency(credit_info.orders_total || 0)}
                </span>
              </div>
              <div>
                <span className="text-gray-500">תשלומים: </span>
                <span className="font-semibold text-green-600">
                  -{formatCurrency(credit_info.collected_amount || 0)}
                </span>
              </div>
              <div>
                <span className="text-gray-500">זיכויים: </span>
                <span className="font-semibold text-orange-600">
                  -{formatCurrency(credit_info.refunds_total || 0)}
                </span>
              </div>
              <div>
                <span className="text-gray-500">= סה"כ חוב: </span>
                <span className={`font-bold ${(credit_info.current_balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(credit_info.current_balance || 0)}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('payments')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${activeTab === 'payments'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              היסטוריית תשלומים ({payments.length})
            </button>
            <button
              onClick={() => setActiveTab('checks')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${activeTab === 'checks'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              צ'קים פתוחים
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${activeTab === 'orders'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              הזמנות
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${activeTab === 'documents'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              מסמכים
            </button>
          </nav>
        </div>

        {/* Tab Content - Payments History */}
        {activeTab === 'payments' && (
          <div className="p-4">
            {payments.length === 0 ? (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">אין תשלומים</h3>
                <p className="mt-1 text-sm text-gray-500">טרם נרשמו תשלומים לחברה זו</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">קבלה</th>
                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">תאריך</th>
                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">סכום</th>
                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">אמצעי</th>
                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 hidden sm:table-cell">נגבה ע"י</th>
                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">סטטוס</th>
                      <th className="px-2 py-2 text-right text-xs font-medium text-gray-500">פעולות</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {payments.map((payment) => (
                      <React.Fragment key={payment.id}>
                        <tr className={`hover:bg-gray-50 ${payment.is_cancelled ? 'bg-red-50 opacity-75' : ''}`}>
                          <td className="px-2 py-3 text-xs font-medium text-gray-900">
                            <div className="flex items-center gap-2">
                              <div className="max-w-[120px] truncate" title={payment.receipt_number}>
                                {payment.receipt_number}
                              </div>
                              {payment.is_cancelled && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 font-semibold">
                                  בוטל
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-xs text-gray-900">
                            {new Date(payment.payment_date).toLocaleDateString('he-IL', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit'
                            })}
                          </td>
                          <td className={`px-2 py-3 whitespace-nowrap text-xs ${payment.is_cancelled ? 'text-red-600 line-through' : 'text-green-600'}`}>
                            {payment.cash_discount ? (
                              <div className="flex flex-col">
                                <span className="font-bold">{formatCurrency(payment.actual_amount)}</span>
                                <span className="text-[10px] text-gray-500">
                                  (קוזז: {formatCurrency(payment.credited_amount)})
                                </span>
                              </div>
                            ) : (
                              <span className="font-bold">{formatCurrency(payment.amount)}</span>
                            )}
                          </td>
                          <td className="px-2 py-3 text-xs text-gray-900">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1">
                              <span>{getPaymentMethodLabel(payment.payment_method)}</span>
                              {payment.payment_method === 'discount' && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700 whitespace-nowrap font-medium">
                                  הנחה כללית
                                </span>
                              )}
                              {payment.cash_discount && payment.payment_method !== 'discount' && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 whitespace-nowrap font-medium">
                                  הנחת מזומן 18%
                                </span>
                              )}
                              {payment.checks?.length > 0 && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 whitespace-nowrap">
                                  {payment.checks.length} צ'קים
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-xs text-gray-900 hidden sm:table-cell">
                            <div className="max-w-[100px] truncate" title={payment.collector?.name}>
                              {payment.collector?.name || '-'}
                            </div>
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap">
                            {getStatusBadge(payment.status)}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-xs space-x-2 space-x-reverse">
                            <button
                              onClick={() => handleDownloadReceipt(payment.id, payment.receipt_number)}
                              className="text-primary-600 hover:text-primary-800 font-medium"
                            >
                              הורד
                            </button>
                            {payment.status === 'pending' && (
                              <>
                                {' | '}
                                <button
                                  onClick={() => handleApprovePayment(payment.id)}
                                  className="text-green-600 hover:text-green-800 font-medium"
                                >
                                  אשר
                                </button>
                                {' | '}
                                <button
                                  onClick={() => handleRejectPayment(payment.id)}
                                  className="text-red-600 hover:text-red-800 font-medium"
                                >
                                  דחה
                                </button>
                                {' | '}
                                <button
                                  onClick={() => openEditModal(payment)}
                                  className="text-blue-600 hover:text-blue-800 font-medium"
                                  title="ערוך פרטי תשלום"
                                >
                                  ערוך
                                </button>
                              </>
                            )}
                            {payment.status === 'approved' && !payment.is_cancelled && (
                              <>
                                {' | '}
                                <button
                                  onClick={() => openEditModal(payment)}
                                  className="text-blue-600 hover:text-blue-800 font-medium"
                                  title="ערוך פרטי תשלום"
                                >
                                  ערוך
                                </button>
                                {' | '}
                                <button
                                  onClick={() => openCancelModal(payment)}
                                  className="text-red-600 hover:text-red-800 font-medium"
                                  title="בטל תשלום"
                                >
                                  בטל
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                        {payment.checks?.length > 0 && <ChecksList payment={payment} />}
                        {payment.is_cancelled && payment.cancellation_reason && (
                          <tr>
                            <td colSpan="7" className="px-2 py-2 text-xs text-red-700 bg-red-50">
                              <div className="flex items-start gap-2">
                                <span className="font-semibold">סיבת ביטול:</span>
                                <span>{payment.cancellation_reason}</span>
                                {payment.cancelled_at && (
                                  <span className="text-red-600 mr-auto">
                                    ({new Date(payment.cancelled_at).toLocaleDateString('he-IL')})
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tab Content - Checks */}
        {activeTab === 'checks' && (
          <div className="p-4">
            <CompanyChecksList
              payments={data?.payments || []}
              onUpdate={fetchCompanyDetails}
            />
          </div>
        )}

        {/* Tab Content - Orders */}
        {activeTab === 'orders' && (
          <div className="p-4">
            <CompanyOrdersList companyId={companyId} />
          </div>
        )}

        {/* Tab Content - Documents */}
        {activeTab === 'documents' && (
          <div className="p-4">
            <CompanyDocuments
              companyId={companyId}
              canUpload={true}
              canDelete={true}
            />
          </div>
        )}
      </div>

      {/* Payment Collection Modal */}
      <PaymentCollectionModal
        isOpen={showCollectionModal}
        onClose={() => setShowCollectionModal(false)}
        preselectedCompany={{
          id: company.id,
          name: company.name,
          current_balance: credit_info?.current_balance || 0,
          available_credit: credit_info?.available_credit || 0
        }}
        onSuccess={handlePaymentSuccess}
      />

      {/* Edit Payment Modal */}
      <EditPaymentModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        payment={paymentToEdit}
        onSuccess={handleEditSuccess}
      />

      {/* Cancel Payment Modal */}
      {showCancelModal && paymentToCancel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" dir="rtl">
            <h3 className="text-xl font-bold text-gray-900 mb-4">ביטול תשלום</h3>

            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800 mb-2">
                <strong>אזהרה:</strong> פעולה זו תבטל את התשלום ותחזיר את הסכום ליתרת החוב של החברה.
              </p>
              <div className="text-sm text-gray-700 mt-2">
                <p><strong>קבלה:</strong> {paymentToCancel.receipt_number}</p>
                <p><strong>סכום:</strong> {formatCurrency(paymentToCancel.amount)}</p>
                <p><strong>תאריך:</strong> {new Date(paymentToCancel.payment_date).toLocaleDateString('he-IL')}</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                סיבת ביטול <span className="text-red-500">*</span>
              </label>
              <textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="הזן את סיבת ביטול התשלום..."
                disabled={cancelling}
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setPaymentToCancel(null);
                  setCancellationReason('');
                }}
                disabled={cancelling}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
              >
                ביטול
              </button>
              <button
                onClick={handleCancelPayment}
                disabled={cancelling || !cancellationReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancelling ? 'מבטל...' : 'אשר ביטול'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discount Modal */}
      {showDiscountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" dir="rtl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <svg className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">הנחה כללית על חוב</h3>
                <p className="text-sm text-gray-500">{company?.name}</p>
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">יתרת חוב נוכחית:</span>
                <span className="text-lg font-bold text-red-600">
                  {formatCurrency(credit_info?.current_balance || 0)}
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  סכום הנחה <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">₪</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(e.target.value)}
                    className="w-full pr-8 pl-4 py-3 text-lg font-semibold border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="0.00"
                    disabled={applyingDiscount}
                  />
                </div>
                {credit_info?.current_balance > 0 && (
                  <button
                    type="button"
                    onClick={() => setDiscountAmount((credit_info.current_balance || 0).toFixed(2))}
                    className="mt-1 text-xs text-purple-600 hover:text-purple-800"
                  >
                    הנח את כל החוב ({formatCurrency(credit_info.current_balance || 0)})
                  </button>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  סיבת ההנחה <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="למשל: הנחה חד פעמית, תיקון טעות חיוב, פיצוי לקוח..."
                  disabled={applyingDiscount}
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => {
                  setShowDiscountModal(false);
                  setDiscountAmount('');
                  setDiscountReason('');
                }}
                disabled={applyingDiscount}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
              >
                ביטול
              </button>
              <button
                onClick={handleApplyDiscount}
                disabled={applyingDiscount || !discountAmount || !discountReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {applyingDiscount ? 'מחיל הנחה...' : 'החל הנחה'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCompanyDebt;
